import React from "react";
import Form from "react-bootstrap/Form";
import { Button } from "react-bootstrap";
import classes from "./QuoteForm.module.scss";
import Header from "../Header";
import {
  CorpCode,
  usePostSaveQuoteMutation,
} from "../../features/rapidstor/rapidstorApiSlice";
import { useImmer } from "use-immer";
import { useLoaderData, useNavigate } from "react-router-dom";
import Location from "../../features/rapidstor/entities/Location";
import { useSelector } from "react-redux";
import { selectUnitData } from "../../store/bookingSlice";
import { setCookie } from "../../utils/cookies";
import { getCookie } from "../../utils/cookies";
import { usePatchReservationMutation } from "../../features/rapidstor/rapidstorApiSlice";
import { BaseReservation } from "@r6digital/rapidstor-types/types/Reservation";
import { UnitData } from "../../features/rapidstor/entities/UnitType";

function QuoteForm() {
  const navigate = useNavigate();
  const unitData = useSelector(selectUnitData);
  const location = useLoaderData() as Location;
  const [emailQuote, { isLoading: emailQuoteLoading }] = usePostSaveQuoteMutation();

  // Only use quoteToken if it matches the current location
  const quoteTokenRaw = decodeURIComponent(getCookie("quoteToken") || "");
  const quoteToken =
    quoteTokenRaw && quoteTokenRaw.startsWith(`${location.locationCode}:`)
      ? quoteTokenRaw.split(":")[1] || null
      : null;
  const [patchReservation] = usePatchReservationMutation();

  function getQuotePayload(form: Record<string, string>, unitData: UnitData) {
    const [firstName, ...lastName] = form.name.split(" ");
    return {
      customListing: true,
      sourceUrl: window.location.href,
      tenant: {
        tenantId: null,
        firstName,
        lastName: lastName.join(" "),
        email: form.email,
        phone: form.phone,
      },
      neededDate: new Date().toISOString(),
      unitType: unitData.unitType._id,
      deal: unitData.unitType.deal?._id || null,
      sendEmail: true,
    };
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!unitData || !unitData.unitType) return;
    const payload = getQuotePayload(form, unitData);

    if (quoteToken) {
      // PATCH logic
      patchReservation({
        reservationId: quoteToken,
        locationCode: location.locationCode,
        payload,
      })
        .then(() => {
          navigate(`/locations/${location.slug}/quote-sent`);
        })
        .catch((err: Error) => {
          console.error(err);
        });
      return;
    }

    emailQuote({
      corpCode: CorpCode,
      locationCode: location.locationCode,
      payload,
    })
      .then((resp: { data?: BaseReservation; error?: unknown }) => {
        // Save token as cookie if present in response
        const token = resp?.data?.token;
        if (token) {
          setCookie("quoteToken", `${location.locationCode}:${token}`);
        }
        navigate(`/locations/${location.slug}/quote-sent`);
      })
      .catch((err: Error) => {
        console.error(err);
      });
  }

  const [form, updateForm] = useImmer<Record<string, string>>({
    name: "",
    email: "",
    phone: "",
  });

  const formFilled = form.name && form.email && form.phone;

  function update(key: string, event: React.BaseSyntheticEvent) {
    updateForm((draft) => {
      draft[key] = String(event.target.value);
      return draft;
    });
  }

  return (
    <>
      <div className={"d-flex flex-column row-gap-3"}>
        <div className={`${classes.header} fw-semibold`}>Email Quote</div>

        <Header level={4} className={"opacity-70"}>
          Please leave your details with us and receive an instant email with your
          preferred unit selection, and links to reserve or book.
        </Header>

        <Form
          className={`${classes.form} d-flex flex-column row-gap-1`}
          onSubmit={submit}
        >
          <Form.Control
            type={"text"}
            placeholder={"Name*"}
            required
            onChange={(e) => update("name", e)}
          />
          <Form.Control
            type={"email"}
            placeholder={"Email*"}
            required
            onChange={(e) => update("email", e)}
          />
          <Form.Control
            type={"tel"}
            placeholder={"Phone*"}
            required
            onChange={(e) => update("phone", e)}
          />

          <Button
            className={"btn-md mt-2"}
            type={"submit"}
            disabled={!formFilled || emailQuoteLoading}
          >
            Submit Form
          </Button>
        </Form>

        <div className={`${classes.disclaimer} text-center mt-2 opacity-50`}>
          Your data privacy is important to us, we will not provide or use this
          information without your consent, or spam your inbox.
        </div>
      </div>
    </>
  );
}

export default QuoteForm;
